//
// RecordInspector
//
// An inspector for the properties of a PIM record (definition manager) and the channels

import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useStore } from '../../../stores'

import { Divider, Spacer, VView } from '../../../appview'
import { Header, Property, Text, usePopoverContext } from '../../../components'
import { useMousePopover } from '../../../hooks/useMousePopover'
import { ItemTitle } from '../../components'
import { ChildDefinitionsMenu } from './ChildDefinitionsMenu'
import { DefinitionMenu } from './DefinitionMenu'
import { ExtendedDefinitionMenu } from './ExtendedDefinitionMenu'
import { LinkedRecordMenu } from './LinkedRecordMenu'
import { RecordInspectorDEBUG } from './RecordInspectorDEBUG'
import { RecordInspectorExtensions } from './RecordInspectorExtensions'
import { SiblingDefinitionsMenu } from './SiblingDefinitionsMenu'

export const RecordInspector = observer(function RecordInspector({
    worksheet,
    viewkey,
}) {
    const { app, view, data, DEBUG } = useStore()

    const [selectedProperty, setSelectedProperty] = useState(null)

    const DefinitionContextMenu = useMousePopover(DefinitionMenu)
    const ExtensionContextMenu = useMousePopover(ExtendedDefinitionMenu)
    const LinkedRecordContextMenu = useMousePopover(LinkedRecordMenu)
    const SiblingDefinitionsContextMenu = useMousePopover(SiblingDefinitionsMenu)
    const ChildDefinitionsContextMenu = useMousePopover(ChildDefinitionsMenu)
    const popoverContext = usePopoverContext()

    const hideContextMenus = () => {
        DefinitionContextMenu.hide()
        ExtensionContextMenu.hide()
        LinkedRecordContextMenu.hide()
        SiblingDefinitionsContextMenu.hide()
        ChildDefinitionsContextMenu.hide()
    }

    const onToggleProperty = (e, property) => {
        hideContextMenus()
        if (selectedProperty === property) {
            setSelectedProperty(null)
        } else {
            setSelectedProperty(property)
        }
        e.preventDefault()
        e.stopPropagation()
    }

    const record = worksheet.record
    const definition = worksheet.definitions.get(record.definition)
    const original_definition =
        definition && definition.is_extended
            ? data.definitions.get(definition.original)
            : null
    const firstchild = worksheet.children.length ? worksheet.children[0] : null

    const refetchAfterMutation = () => {
        return worksheet.refetch()
    }

    const onRevealOriginal = () => {
        if (original_definition) {
            view.schemaworksheet.setItem(original_definition)
            view.schematree.datamanager.setItem(original_definition)
            view.setWorkspace('schema')
        }
    }

    const onRevealDefinition = () => {
        view.schemaworksheet.setItem(definition)
        view.schematree.datamanager.setItem(definition)
        view.setWorkspace('schema')
    }

    const onStartExtension = name => {
        const options = name ? { name } : {}
        return record.cloneExtendedDefinition(options).then(refetchAfterMutation)
    }

    const onChangeDefinition = definition_gid => {
        return record.setDefinition(definition_gid).then(refetchAfterMutation)
    }

    const onChangeExtension = definition_gid => {
        return record.setDefinition(definition_gid).then(refetchAfterMutation)
    }

    const onDropExtension = () => {
        if (!original_definition) return
        return record.setDefinition(original_definition.gid).then(refetchAfterMutation)
    }

    const onGotoLinkedRecord = () => {
        return data.fetchRecordsByGid([record.canonical]).then(result => {
            if (result.length) {
                view.pimworksheet.setRecord(result[0])
                view.pimtree.datamanager.setRecord(result[0])
            }
        })
    }

    const onUnlinkRecord = () => {
        record.unlink()
    }

    const onCancel = () => {}

    const onRecordInspectorAction = (action, parameter) => {
        const actions = {
            reveal_original: onRevealOriginal,
            reveal_definition: onRevealDefinition,
            change_definition: onChangeDefinition,
            start_extension: onStartExtension,
            change_extension: onChangeExtension,
            drop_extension: onDropExtension,
            goto_linked_record: onGotoLinkedRecord,
            unlink_record: onUnlinkRecord,
            cancel: onCancel,
        }
        if (!(action in actions)) {
            console.log(`onRecordInspectorAction: unhandled action '${action}'`)
            return
        }
        popoverContext.store.show([...popoverContext.stack])
        actions[action](parameter)
    }

    if (!definition) {
        return null
    }

    const layout = worksheet.layouts.get(definition.layout)
    const DebugInfo = DEBUG ? (
        <>
            <Spacer size={50} />
            <Divider />
            <RecordInspectorDEBUG
                worksheet={worksheet}
                definition={definition}
                layout={layout}
            />
        </>
    ) : undefined

    const definition_validation = worksheet.definition_validation
    const sibling_definitions_validation = worksheet.sibling_definitions_validation
    const child_definitions_validation = worksheet.child_definitions_validation

    const extension_label = definition.is_extended
        ? definition.name
        : app.text('not extended')

    return (
        <VView grow className="panel properties">
            <Header>{app.text('Record info')}</Header>
            <DefinitionContextMenu.Panel
                worksheet={worksheet}
                onAction={onRecordInspectorAction}
            />
            <ExtensionContextMenu.Panel
                worksheet={worksheet}
                onAction={onRecordInspectorAction}
            />
            <LinkedRecordContextMenu.Panel
                record={record}
                onAction={onRecordInspectorAction}
            />
            <SiblingDefinitionsContextMenu.Panel worksheet={worksheet} />
            <ChildDefinitionsContextMenu.Panel worksheet={worksheet} />
            <Property
                className="cc-packed"
                label={app.text('definition')}
                selected={selectedProperty === 'definition'}
                onClick={e => onToggleProperty(e, 'definition')}
                onContextMenu={e => {
                    setSelectedProperty('definition')
                    DefinitionContextMenu.onShow(e)
                }}
            >
                <Text>
                    <ItemTitle
                        item={original_definition || definition}
                        ellipsisAfter={32}
                    />
                    {definition_validation ? (
                        <span className="role cc-notice">
                            {app.text(definition_validation)}
                        </span>
                    ) : (
                        ''
                    )}
                    {definition.is_extended ? (
                        <span className="role">{app.text('extended')}</span>
                    ) : (
                        ''
                    )}
                </Text>
            </Property>
            <Property
                className="cc-packed cc-ellipsis-value"
                label={app.text('extension')}
                selected={selectedProperty === 'extension'}
                onClick={e => onToggleProperty(e, 'extension')}
                onContextMenu={e => {
                    setSelectedProperty('extension')
                    ExtensionContextMenu.onShow(e)
                }}
            >
                <Text
                    className={definition.is_extended ? undefined : 'cc-dimmed'}
                    title={definition.is_extended ? extension_label : undefined}
                >
                    {extension_label}
                </Text>
            </Property>
            {record.is_link ? (
                <Property
                    className="cc-packed"
                    label={app.text('record')}
                    selected={selectedProperty === 'linked'}
                    onClick={e => onToggleProperty(e, 'linked')}
                    onContextMenu={e => {
                        setSelectedProperty('linked')
                        LinkedRecordContextMenu.onShow(e)
                    }}
                >
                    <Text>{app.text('linked')}</Text>
                </Property>
            ) : undefined}
            <Property
                className="cc-packed"
                label={app.text('siblings')}
                selected={selectedProperty === 'siblings'}
                onClick={e => onToggleProperty(e, 'siblings')}
                onContextMenu={e => {
                    setSelectedProperty('siblings')
                    SiblingDefinitionsContextMenu.onShow(e)
                }}
            >
                <Text>
                    {sibling_definitions_validation.length
                        ? sibling_definitions_validation.map(validation => (
                              <span key={validation} className="role cc-notice">
                                  {app.text(validation)}
                              </span>
                          ))
                        : app.text('shared')}
                </Text>
            </Property>
            {!record.is_link &&
            (definition.childdefinitions.length || record.childcount > 0) ? (
                <>
                    <Property
                        className="cc-packed"
                        label={app.text('children')}
                        selected={selectedProperty === 'children'}
                        onClick={e => onToggleProperty(e, 'children')}
                        onContextMenu={e => {
                            setSelectedProperty('children')
                            if (firstchild) {
                                ChildDefinitionsContextMenu.onShow(e)
                            }
                        }}
                    >
                        <Text>
                            {child_definitions_validation.length
                                ? child_definitions_validation.map(validation => (
                                      <span key={validation} className="role cc-notice">
                                          {app.text(validation)}
                                      </span>
                                  ))
                                : app.text('shared')}
                        </Text>
                    </Property>
                </>
            ) : undefined}
            {definition.is_extended ? (
                <>
                    <Spacer size={5} />
                    <Divider />
                    <RecordInspectorExtensions
                        worksheet={worksheet}
                        viewkey={viewkey}
                    />
                </>
            ) : undefined}

            {DebugInfo}
        </VView>
    )
})
